import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AddUpdateDeleteMessageModel,
  AddUpdateDeleteResponseModel,
  StatementMessagingMessageModel,
  TextMessageModel,
} from '../../core/interfaces/statement-messaging-run.model';
import { FundResponse } from '../../core/interfaces/fund.model';

@Injectable({
  providedIn: 'root',
})
export class StatementMessagingHttpClientService {
  private readonly timeout = 30000;

  private readonly common_headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Consumer-Application-Code': 'MU3',
  });

  constructor(private http: HttpClient) {}

  /**
   * Get message details
   */
  getMessageDetails(): Observable<StatementMessagingMessageModel> {
    const url = `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.MESSAGE}`;

    return this.http
      .get<StatementMessagingMessageModel>(url, { headers: this.common_headers })
      .pipe(
        timeout(this.timeout),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  /**
   * Get message details
   */
  getFundIdData(offset: number, limit: number): Observable<FundResponse> {
    const url = `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.FUND_ID}`;

    return this.http
      .get<FundResponse>(url, { headers: this.common_headers, params: { offset, limit } })
      .pipe(
        timeout(this.timeout),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  addMessage(
    addUpdateDeleteMessageModel: AddUpdateDeleteMessageModel,
  ): Observable<AddUpdateDeleteResponseModel> {
    const url = `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.ADD_UPDATE_DELETE_MESSAGE}`;
    return this.http
      .post<AddUpdateDeleteResponseModel>(url, addUpdateDeleteMessageModel, {
        headers: this.common_headers,
      })
      .pipe(
        timeout(this.timeout),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  updateDeleteMessage(
    addUpdateDeleteMessageModel: AddUpdateDeleteMessageModel,
  ): Observable<AddUpdateDeleteResponseModel> {
    const url = `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.ADD_UPDATE_DELETE_MESSAGE}`;
    return this.http
      .patch<AddUpdateDeleteResponseModel>(url, addUpdateDeleteMessageModel, {
        headers: this.common_headers,
      })
      .pipe(
        timeout(this.timeout),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  getMessageTextById(messageId: string): Observable<TextMessageModel[]> {
    const url = `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.TEXT_MESSAGE_BY_ID}`;
    // Build query parameters
    let params = new HttpParams();
    params = params.set('messageId', messageId);
    return this.http.get<TextMessageModel[]>(url, { headers: this.common_headers, params }).pipe(
      timeout(this.timeout),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Handle HTTP errors
   * Changed to arrow function to preserve 'this' context
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Network Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 0:
          errorMessage = 'Network error. Please check your connection.';
          break;
        case 400:
          errorMessage = 'Bad Request: Invalid data provided.';
          break;
        default:
          errorMessage = `Error ${error.status}: ${error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  };
}
